import {
  Button,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { dismissStudent, type Student } from '../api/students';
import ConfirmDismissDialog from './ConfirmDismissDialog';
import { getWaitTimeInfo, formatWaitTime } from '../utils/waitTime';

interface StudentItemProps {
  student: Student;
  onDismissed?: () => void | Promise<unknown>;
  canDismiss?: boolean;
}

export default function StudentItem({ student, onDismissed, canDismiss = true }: StudentItemProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDismissClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDismiss = async () => {
    setConfirmOpen(false);
    try {
      await dismissStudent(student.id);
      await onDismissed?.();
      toast.success('Student dismissed');
    } catch (e) {
      toast.error('Failed to dismiss');
    }
  };

  const handleCancelDismiss = () => {
    setConfirmOpen(false);
  };

  const checkedInAt = dayjs(student.checked_in_at);
  const dismissedAt = student.dismissed_at ? dayjs(student.dismissed_at) : null;
  const isWaiting = !dismissedAt;
  const waitTimeInfo = isWaiting ? getWaitTimeInfo(student) : null;

  return (
    <>
      <TableRow
        hover
        sx={{
          backgroundColor: isWaiting ? '#FFFFFF' : '#F5F5F5',
          borderLeft: isWaiting
            ? waitTimeInfo?.priority === 'urgent'
              ? '5px solid #C62828'
              : waitTimeInfo?.priority === 'moderate'
              ? '5px solid #F57C00'
              : '5px solid #00897B'
            : 'none',
          opacity: isWaiting ? 1 : 0.7,
          transition: 'all 0.3s ease',
        }}
      >
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={600}>{`${student.first_name} ${student.last_name}`}</Typography>
            {waitTimeInfo && waitTimeInfo.priority === 'urgent' && (
              <Chip
                icon={<PriorityHighIcon sx={{ fontSize: 14 }} />}
                label="Urgent"
                size="small"
                sx={{
                  bgcolor: waitTimeInfo.backgroundColor,
                  color: waitTimeInfo.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>{student.grade}</TableCell>
        <TableCell>
          <Chip
            label={student.pickup_type === 'car' ? 'Car Line' : 'Walker'}
            size="small"
            sx={{
              backgroundColor: student.pickup_type === 'car' ? '#E0F2F1' : '#EFEBE9',
              color: student.pickup_type === 'car' ? '#00695C' : '#5D4037',
              borderRadius: 2,
              fontWeight: 500,
              border: 'none',
            }}
          />
        </TableCell>
        <TableCell>
          <Tooltip title={checkedInAt.format('MMM D, YYYY • h:mm A')}>
            <span>{checkedInAt.format('h:mm A')}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          {dismissedAt ? (
            <Tooltip title={dismissedAt.format('MMM D, YYYY • h:mm A')}>
              <span>{dismissedAt.format('h:mm A')}</span>
            </Tooltip>
          ) : waitTimeInfo ? (
            <Chip
              label={`Waiting: ${formatWaitTime(waitTimeInfo.minutes)}`}
              size="small"
              sx={{
                bgcolor: waitTimeInfo.backgroundColor,
                color: waitTimeInfo.color,
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip label="Waiting" size="small" />
          )}
        </TableCell>
        <TableCell align="right">
          {dismissedAt ? (
            <Typography variant="body2" color="text.secondary">
              Dismissed
            </Typography>
          ) : canDismiss ? (
            <Button variant="contained" color="error" onClick={handleDismissClick}>
              Dismiss
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Waiting
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <ConfirmDismissDialog
        open={confirmOpen}
        student={student}
        onConfirm={handleConfirmDismiss}
        onCancel={handleCancelDismiss}
      />
    </>
  );
}
